import { useState, useReducer, useEffect, useRef, type ReactNode } from 'react';

import { Routes, Route, Navigate, useLocation } from 'react-router-dom';

import { ChakraProvider } from '@chakra-ui/react';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { AuthContext } from 'contexts/AuthContext';

import authReducer from 'reducers/authReducer';

import Storage from 'storage';

import { useAppSelector } from 'hooks';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import { hasCompleteSession } from 'utils/session';

import AdminLayout from 'layouts/admin/admin';

import initialTheme from 'theme/theme';

import SignIn from 'views/signin';

import PublicValidationsView from 'views/public_validations/PublicValidationsView';
import PublicUploads from 'views/public_uploads/PublicUploads';
import ImpersonateView from 'views/impersonate/ImpersonateView';

import { BlockingNotificationModal } from 'components/notifications/BlockingNotificationModal';
import ThrottledRequestModal from 'components/modals/ThrottledRequestModal';

import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

import 'assets/css/App.css';
import 'assets/css/DateRangePicker.css';
import 'assets/css/ReactDatePicker.css';

import { pdfjs } from 'react-pdf';

import { User } from 'reducers/authReducer/authReducer.types';

import * as Sentry from '@sentry/react';

if (!!process.env.REACT_APP_SENTRY_DSN) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.REACT_APP_ENV,
    release: process.env.REACT_APP_COMMIT_SHA,
    sendDefaultPii: true,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    // Performance Monitoring
    tracesSampleRate: 0.05,
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/api\.big\.policypilot\.ai\//,
      /^https:\/\/api\.dev\.big\.policypilot\.ai\//,
    ],
    // Session Replay
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
  });
}

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const queryClient = new QueryClient();

const noopStorageManager = {
  get: () => 'light' as 'light' | 'dark', // Always return light mode
  set: () => {}, // Don't save anything
  type: 'localStorage' as const,
};

const VIEW_AS_KEYS = ['view_as_email', 'view_as_branch_id'];

const viewAsQuery = (search: string) => {
  const from = new URLSearchParams(search);
  const kept = new URLSearchParams();
  VIEW_AS_KEYS.forEach((key) => {
    const value = from.get(key);
    if (value) kept.set(key, value);
  });
  return kept.toString();
};

const ViewAsRouteGuard = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const isViewAsEnabled = useFeatureFlag('enable-desktop-view-as');
  const featureFlagsLoaded = useAppSelector(
    (state) => Object.keys(state.featureFlags.flags).length > 0,
  );
  const carried = useRef('');

  const inUrl = viewAsQuery(location.search);
  const isHome = location.pathname === '/admin/home';
  const wanted = !(featureFlagsLoaded && isViewAsEnabled)
    ? inUrl
    : isHome
      ? ''
      : inUrl || carried.current;

  carried.current = isHome ? '' : wanted;

  if (wanted === inUrl) return <>{children}</>;

  const params = new URLSearchParams(location.search);
  VIEW_AS_KEYS.forEach((key) => params.delete(key));
  new URLSearchParams(wanted).forEach((value, key) => params.set(key, value));
  const search = params.toString();

  return (
    <Navigate
      replace
      to={{
        pathname: location.pathname,
        search: search ? `?${search}` : '',
        hash: location.hash,
      }}
    />
  );
};

const App = () => {
  const [currentTheme, setCurrentTheme] = useState(initialTheme);
  const [{ user, environment }, authDispatch] = useReducer(authReducer, {
    user: Storage.get('user') as User,
    environment: Storage.get('environment') as string,
  });

  // Clear any existing color mode from localStorage on mount
  useEffect(() => {
    localStorage.removeItem('chakra-ui-color-mode');
  }, []);

  // Log out user if the cookie or any session storage key is missing
  useEffect(() => {
    if (user && !hasCompleteSession()) {
      authDispatch({ type: 'remove_user' });
    }
  }, [user, authDispatch]);

  // Log out user if local storage is cleared or reload if modified
  useEffect(() => {
    if (!user) return;

    const handleStorage = () => {
      if (!hasCompleteSession()) {
        authDispatch({ type: 'remove_user' });
        return;
      }

      if (Storage.get('user')?.token !== user.token) {
        window.location.reload();
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [user, authDispatch]);

  return (
    <QueryClientProvider client={queryClient}>
      <ChakraProvider
        theme={currentTheme}
        colorModeManager={noopStorageManager}
      >
        <AuthContext.Provider value={{ user, environment, authDispatch }}>
          {user && <BlockingNotificationModal />}
          <ThrottledRequestModal />
          <Routes>
            <Route path="/impersonate" element={<ImpersonateView />} />
            <Route
              path="/validations/:uuid"
              element={<PublicValidationsView />}
            />
            <Route path="/upload/policy/:uuid" element={<PublicUploads />} />
            {user ? (
              <>
                <Route
                  path="admin/*"
                  element={
                    <ViewAsRouteGuard>
                      <AdminLayout
                        theme={currentTheme}
                        setTheme={setCurrentTheme}
                      />
                    </ViewAsRouteGuard>
                  }
                />
                <Route path="*" element={<Navigate to="/admin" />} />
              </>
            ) : (
              <>
                <Route path="/auth/sign-in" element={<SignIn />} />
                <Route path="*" element={<Navigate to="/auth/sign-in" />} />
              </>
            )}
          </Routes>
        </AuthContext.Provider>
      </ChakraProvider>
    </QueryClientProvider>
  );
};

export default App;
